import React from "react";
import PanResponderAdapter from "./PanResponderAdapter";
import {
  Layout,
  NavigationState,
  PagerProps,
  Route,
  SceneRendererProps,
} from "./types";
import SceneView from "./SceneView";
import {
  StyleProp,
  View,
  StyleSheet,
  ViewStyle,
  LayoutChangeEvent,
} from "react-native";
import { Pagination } from "../Pagination";

export type Props<T extends Route> = PagerProps & {
  initialLayout?: Partial<Layout>;
  renderScene: (props: SceneRendererProps & { route: T }) => React.ReactNode;
  navigationState: NavigationState<T>;
  onIndexChange: (index: number) => void;
  renderLazyPlaceholder?: (props: { route: T }) => React.ReactNode;
  lazy?: ((props: { route: T }) => boolean) | boolean;
  lazyPreloadDistance?: number;
  sceneContainerStyle?: StyleProp<ViewStyle>;
  pagerStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
  showPagination?: boolean;
  paginationAdornment?: React.ReactNode;
};

export default function Pager<T extends Route>({
  onIndexChange,
  navigationState,
  renderScene,
  initialLayout,
  keyboardDismissMode = "auto",
  lazy = false,
  lazyPreloadDistance = 0,
  onSwipeStart,
  onSwipeEnd,
  renderLazyPlaceholder = () => null,
  sceneContainerStyle,
  pagerStyle,
  style,
  swipeEnabled = true,
  animationEnabled = true,
  showPagination = true,
  paginationAdornment,
}: Props<T>) {
  const [layout, setLayout] = React.useState({
    width: 0,
    height: 0,
    ...initialLayout,
  });

  const jumpToIndex = (index: number) => {
    if (index !== navigationState.index) {
      onIndexChange(index);
    }
  };

  const handleLayout = (e: LayoutChangeEvent) => {
    const { height, width } = e.nativeEvent.layout;
    setLayout((prevLayout) => {
      if (prevLayout.width === width && prevLayout.height === height) {
        return prevLayout;
      }

      return { height, width };
    });
  };

  const pagination = showPagination ? (
    showPagination ? (
      <Pagination
        currentPage={navigationState.index + 1}
        totalPages={navigationState.routes.length}
        goToPage={(index) => onIndexChange(index - 1)}
        endAdornment={paginationAdornment}
      />
    ) : null
  ) : null;

  return (
    <View onLayout={handleLayout} style={[styles.pager, style]}>
      <PanResponderAdapter
        layout={layout}
        navigationState={navigationState}
        keyboardDismissMode={keyboardDismissMode}
        swipeEnabled={swipeEnabled}
        onSwipeStart={onSwipeStart}
        onSwipeEnd={onSwipeEnd}
        onIndexChange={jumpToIndex}
        animationEnabled={animationEnabled}
        style={pagerStyle}
      >
        {({ position, render, addEnterListener, jumpTo }) => {
          // All of the props here must not change between re-renders
          // This is crucial to optimizing the routes with PureComponent
          const sceneRendererProps = {
            position,
            layout,
            jumpTo,
          };

          return (
            <React.Fragment>
              {render(
                navigationState.routes.map((route, i) => {
                  return (
                    <SceneView
                      {...sceneRendererProps}
                      addEnterListener={addEnterListener}
                      key={route.key}
                      index={i}
                      lazy={typeof lazy === "function" ? lazy({ route }) : lazy}
                      lazyPreloadDistance={lazyPreloadDistance}
                      navigationState={navigationState}
                      style={sceneContainerStyle}
                    >
                      {({ loading }) =>
                        loading
                          ? renderLazyPlaceholder({ route })
                          : renderScene({
                              ...sceneRendererProps,
                              route,
                            })
                      }
                    </SceneView>
                  );
                })
              )}
            </React.Fragment>
          );
        }}
      </PanResponderAdapter>
      {pagination}
    </View>
  );
}

const styles = StyleSheet.create({
  pager: {
    flex: 1,
    overflow: "hidden",
  },
});
